/** A flag's scope as the config API stores it: an "all" master switch per axis, plus an explicit list. */
export interface FlagScope {
  is_all_sub_orgs: boolean;
  sub_organization_ids: number[];
  is_all_lobs: boolean;
  lobs: string[];
}

/**
 * Every member an "all" scope covers, per axis — read from what the scope check itself matches, not from
 * what the dialog's pickers offer: the org's sub-orgs, and the org's whole claim-type list, because
 * `is_all_lobs` matches a claim's LoB whatever it is. An empty universe means the axis covers nothing that
 * can fall out of scope: that is how an org with sub-orgs disabled is passed in.
 */
export interface FlagScopeUniverse {
  subOrganizationIds: number[];
  lobs: string[];
}

export interface RemovedFlagScope {
  subOrganizationIds: number[];
  lobs: string[];
}

const removedMembers = <T>(previousIsAll: boolean, previous: T[], nextIsAll: boolean, next: T[], all: T[]): T[] => {
  if (nextIsAll) {
    return [];
  }
  const kept = new Set(next);
  return (previousIsAll ? all : previous).filter((member) => !kept.has(member));
};

/**
 * The sub-orgs and LoBs the edited scope stops covering. Applied flags are never revoked when a flag's
 * scope changes, so this is what the admin has to be warned about before saving.
 *
 * A member is "removed" when the previous scope covered it and the new one doesn't. Switching an axis to
 * "all" (or extending its list) therefore reports nothing, and so does replacing "all" with a list of every
 * member — coverage of the org's existing claims is unchanged, only future members drop out.
 */
export const getRemovedFlagScope = (
  previous: FlagScope,
  next: FlagScope,
  universe: FlagScopeUniverse
): RemovedFlagScope => ({
  subOrganizationIds: removedMembers(
    previous.is_all_sub_orgs,
    previous.sub_organization_ids,
    next.is_all_sub_orgs,
    next.sub_organization_ids,
    universe.subOrganizationIds
  ),
  lobs: removedMembers(previous.is_all_lobs, previous.lobs, next.is_all_lobs, next.lobs, universe.lobs),
});

export const hasRemovedFlagScope = (removed: RemovedFlagScope): boolean =>
  removed.subOrganizationIds.length > 0 || removed.lobs.length > 0;
